import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { motion, useReducedMotion } from 'motion/react';
import { Sparkles, ArrowRight, BookOpen, Lock } from 'lucide-react';
import { useOwnedConfetti, OWNED_CONFETTI_CANVAS_CLASS } from '@/shared/hooks/useOwnedConfetti';
import { Helmet } from 'react-helmet';
import { Link, Navigate, useInRouterContext } from 'react-router-dom';
import type { ExamStatus } from '@/models/UserTypes';
import { applyInternalTrafficFromQuery, isInternalTraffic } from '@/utils/internalTraffic';
import { trackMetaEvent } from '@/utils/metaPixel';
import { trackRedditEvent, trackRedditCustomEvent } from '@/utils/redditPixel';
import { trackGtagEvent } from '@/utils/Analytics';
import PassPlanCapture from '@/components/PassPlanCapture';
import { abVariant } from '@/utils/abVariant';
import { STRIPE_PAYMENT_LINKS as PAYMENT_LINKS } from '@/config/stripePaymentLinks';
import {
  clearDiagnosticProgress,
  clearDiagnosticResult,
  markDiagnosticCaptured,
  readDiagnosticProgress,
  readDiagnosticResult,
  saveDiagnosticProgress,
  saveDiagnosticResult,
  type DiagnosticExamType,
  type StoredDiagnostic,
} from '@/utils/diagnosticMemory';
import { savePassPlanToken } from '@/utils/passPlanAccess';
import { publicAsset } from '@/utils/publicAsset';
import {
  AREAS,
  DIAGNOSTIC,
  parseDiagnosticPrefill,
  type DiagnosticQuestion,
} from '@/data/diagnosticBank';
import { appHref } from '@/utils/appHref';
import { useOptionalAuth } from '@/contexts/AuthContext';
import { usePublicFunnelGate } from '@/hooks/usePublicFunnelGate';
import { hasPaidAccess } from '@/utils/firebase/subscription';
import { isEmbeddedCheckoutEnabled, isPassPlanTrialAtMintEnabled, isTrialWeekOptionCEnabled } from '@/config/featureFlags';
import { AppHandoffCard, examDayLabel } from '@/shared/components/AppHandoffCard';
import { armHandoffFirstAnswer, handoffStartPath, handoffVariant, saveHandoffResults } from '@/utils/handoffVariant';
import { saveAimProfile, warmTrialPlan } from '@/features/onboarding/aimProfile';
import { FilledReadinessGauge, ResultsBreakdown } from '@/features/trialWeek/DiagnosticResults';
import { clearPassPlanMint } from '@/utils/passPlanAuth';

// The optional post-result segmentation question (design §7b). Pre-account, so
// it captures only the status enum — piped through the trial CTA into onboarding,
// which pre-fills the scheduler so the question is never asked twice.
const SCHEDULE_CHOICES: { status: ExamStatus; label: string }[] = [
  { status: 'scheduled', label: 'I have a date' },
  { status: 'approved', label: 'Approved, booking soon' },
  { status: 'waiting', label: 'Waiting on board approval' },
  { status: 'accruing', label: 'Still accruing hours' },
  { status: 'exploring', label: 'Just exploring' },
];

/**
 * Public, no-signup ASWB DIAGNOSTIC — the marketing funnel's linchpin (GTM step ②).
 *
 * Flow: answer 6 real questions (2026 format: 3 areas, 3 choices) → get a personalized weak-area
 * report + estimated readiness → convert via Stripe Payment Links.
 * No signup wall: payment-link buyers are reconciled by email server-side.
 *
 * This is the "answerable question" engine surfaced as a diagnostic — it demos the
 * product (find your gaps) AND is the destination for all outreach.
 *
 * SLICE 1 (this file): client-side diagnostic + weak-area report.
 * Email-capture gate + captureLead still write `leads/{hash}` for logged-out
 * Pass Plan / Pam nurture. After Firebase auth, the same snapshot is copied
 * onto `users/{uid}` via createTrialUser / updateUserProfile.
 *
 * Questions are curated from the "LCSW free quiz" set (src/utils/questions.json) —
 * already designated free content, written by Pam.
 *
 * Also the diagnostic-first HOMEPAGE ('/'): rather than duplicate the 6-question
 * bank + scoring + email-capture + UTM/internal-traffic logic, `Landing.tsx` renders
 * THIS component directly with a handful of optional framing props (hero copy above
 * the intro, an equity section after the result, a different canonical/meta). Every
 * default below matches the original /try-only behavior exactly, so this component
 * is unchanged for the /try route.
 */

// One-time "$129 access until you pass" (the primary offer). Buyers land with
// no uid — the webhook reconciles them by email (reconcilePaymentLinkUser) and
// grants hasLifetimeAccess. Links live in @/config/stripePaymentLinks (shared
// with Pricing so the two CTAs can't drift).

// The 6-question bank + the three 2026 content areas now live in
// `@/data/diagnosticBank` so `/q` (the answer-in-email callback) can teach from
// exactly the content this page grades. Content and ordering are unchanged.

// Official reference the interjection links to: ASWB's own content-outlines page,
// which lists the content areas (and KSAs) each exam category maps to.
const ASWB_OUTLINE_URL = 'https://www.aswb.org/exam/readiness-to-practice/content-outlines/';

const card: React.CSSProperties = { boxShadow: '0 6px 0 rgba(17,17,17,0.06)' };

const GATE_EXPERIMENT = 'try_gate_copy_short';
// How long the handoff bridge waits for userData before falling back to the report.
export const HANDOFF_PROFILE_WAIT_MS = 12_000;

const EXAM_TYPES = ['LBSW', 'LMSW', 'LCSW'] as const;

/**
 * The `?exam=` the newsletter callback carries over from `/q`. Read ONLY when a
 * valid `?prefill=` is also present, so the no-prefill intro (the homepage and
 * every existing /try link) still opens with no exam chosen, exactly as before.
 */
const readExamParam = (): DiagnosticExamType | null => {
  if (typeof window === 'undefined') return null;
  const raw = (new URLSearchParams(window.location.search).get('exam') || '').trim().toUpperCase();
  return (EXAM_TYPES as readonly string[]).includes(raw) ? (raw as DiagnosticExamType) : null;
};

const DEFAULT_INTRO_TITLE = 'Free ASWB Practice Test (LCSW, LMSW & LBSW): 6 questions, 3 min, no account';
const DEFAULT_META_DESCRIPTION =
  'Free ASWB practice test: 6 real LCSW, LMSW & LBSW questions in the 2026 format, with rationales. Finish, enter your email, and your readiness score, weakest area, and Pass Plan are yours. No account, no card.';

interface TryAQuestionProps {
  /** Landing on `/` only: drop funnel events for a visitor who arrived signed in (see usePublicFunnelGate). */
  gateSignedInArrivals?: boolean;
  /** Canonical path this instance is mounted at. Default '/try'. */
  canonicalPath?: string;
  /** <title> on the intro stage (quiz/report stages keep their own dynamic titles). */
  introPageTitle?: string;
  metaDescription?: string;
  ogTitle?: string;
  ogImage?: string;
  /** Schema.org JSON-LD, only rendered when provided (intro stage). */
  structuredData?: Record<string, unknown>;
  /** Rendered as the intro card's top zone, intro stage only — e.g. a hero for '/'. */
  heroSection?: React.ReactNode;
  /** When true, `heroSection` is the ATTACHED top zone of the intro card — the two
   *  render as one continuous card (teal promise → white action), and the intro's
   *  own badge/headline/description are suppressed so the hero isn't restated.
   *  Homepage only; `/try` leaves this false and shows the full standalone intro. */
  attachedHero?: boolean;
  /** Replaces the default "— Pam, your clinical supervisor" line under the intro card. */
  introFootnote?: React.ReactNode;
  /** Rendered on the report stage, right after the score card. */
  equitySection?: React.ReactNode;
  /** Rendered below the intro card, intro stage only — social proof near the
   *  funnel top. Homepage only; `/try` leaves this unset. */
  belowIntroSection?: React.ReactNode;
}

/**
 * The HELD gauge on the capture stage — the same teal instrument as the filled
 * reveal, but LOCKED: an indeterminate arc gently sweeps (teasing that a number
 * is waiting) with a lock at its center and NO score. It leaks nothing about how
 * they did; the real value only draws in once they trade their email. Mirrors the
 * .pen "Readiness Hero" ring so the reveal that follows lands in the same shape.
 */
const LockedReadinessGauge: React.FC = () => {
  const reduce = useReducedMotion();
  const size = 140;
  const stroke = 13;
  const r = (size - stroke) / 2;
  const c = size / 2;
  const circ = 2 * Math.PI * r;
  // A short arc (a quarter of the ring) that rotates — a deliberately meaningless
  // placeholder, never the visitor's score.
  const arc = circ * 0.25;
  return (
    <div className="relative flex-shrink-0" style={{ width: size, height: size }} aria-hidden="true">
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle cx={c} cy={c} r={r} fill="none" stroke="rgba(255,255,255,0.16)" strokeWidth={stroke} />
        <motion.circle
          cx={c}
          cy={c}
          r={r}
          fill="none"
          stroke="rgba(255,255,255,0.55)"
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={`${arc} ${circ - arc}`}
          style={{ transformOrigin: 'center' }}
          initial={reduce ? false : { rotate: -90 }}
          animate={reduce ? { rotate: -90 } : { rotate: 270 }}
          transition={reduce ? undefined : { duration: 1.6, ease: 'linear', repeat: Infinity }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <Lock className="w-7 h-7 text-white" strokeWidth={2.5} />
        <span className="mt-1 text-[10px] font-extrabold uppercase tracking-[0.15em] text-white/60">Locked</span>
      </div>
    </div>
  );
};

/**
 * Pam's between-question interjection — the moment that makes the diagnostic feel
 * like the real supervised quiz. She reacts with energy and teaches the concept,
 * then points to the official ASWB content outline, WITHOUT saying right or wrong
 * (no verdict, no green/red) so the visitor can't tally a score before the reveal.
 * Plum "Pam" surface (.pen "Pam's Tip"), avatar, ledge, gentle pop-in.
 */
const PamInterjection: React.FC<{ q: DiagnosticQuestion; isLast: boolean; onContinue: () => void }> = ({ q, isLast, onContinue }) => {
  const reduce = useReducedMotion();
  return (
    <motion.div
      className="mt-5 rounded-2xl border-2 border-accent-200 bg-accent-50 p-5"
      style={{ boxShadow: '0 4px 0 0 rgba(139,90,124,0.20)' }}
      initial={reduce ? false : { opacity: 0, y: 12, scale: 0.985 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ duration: 0.35, ease: 'easeOut' }}
    >
      <div className="flex items-start gap-3">
        <img src={publicAsset('pamela-image.png')} alt="" className="w-11 h-11 rounded-full object-cover flex-shrink-0 ring-2 ring-accent-200" />
        <div className="min-w-0">
          <p className="flex items-center gap-1.5 text-[11px] font-extrabold uppercase tracking-wider text-accent-700 mb-1">
            <Sparkles className="w-3.5 h-3.5" /> Pam&apos;s read
          </p>
          <p className="text-[15px] font-bold text-neutral-900 leading-snug">{q.pam}</p>
        </div>
      </div>
      <p className="mt-3 text-sm text-neutral-700 leading-relaxed">{q.why}</p>
      <a
        href={ASWB_OUTLINE_URL}
        target="_blank"
        rel="noopener noreferrer"
        className="mt-3 inline-flex items-center gap-1.5 text-xs font-bold text-accent-700 hover:underline"
      >
        <BookOpen className="w-3.5 h-3.5" /> Official ASWB content outline: {q.cat} ↗
      </a>
      <button
        onClick={onContinue}
        className="mt-4 w-full inline-flex items-center justify-center gap-2 px-4 py-3.5 bg-primary-500 hover:bg-primary-600 text-white rounded-xl font-bold text-base transition-colors"
        style={{ boxShadow: '0 4px 0 #1B3A38' }}
      >
        {isLast ? 'Reveal my readiness' : 'Next question'}
        <ArrowRight className="w-[18px] h-[18px]" />
      </button>
    </motion.div>
  );
};


// A plain <a href> into /onboarding cold-booted the whole app, and the shell's auth
// wait showed "Loading Your Account". The fallback covers unit tests that mount /try without a router.
const InAppLink: React.FC<React.AnchorHTMLAttributes<HTMLAnchorElement> & { to: string }> = ({ to, ...rest }) =>
  useInRouterContext() ? <Link to={to} {...rest} /> : <a href={appHref(to)} {...rest} />;

const ForwardTo: React.FC<{ to: string }> = ({ to }) => {
  const inRouter = useInRouterContext();
  useEffect(() => {
    if (!inRouter) window.location.assign(appHref(to));
  }, [inRouter, to]);
  return inRouter ? <Navigate to={to} replace /> : null;
};

const TryAQuestion: React.FC<TryAQuestionProps> = ({
  canonicalPath = '/try',
  gateSignedInArrivals = false,
  introPageTitle = DEFAULT_INTRO_TITLE,
  metaDescription = DEFAULT_META_DESCRIPTION,
  ogTitle,
  ogImage,
  structuredData,
  heroSection,
  attachedHero = false,
  introFootnote,
  equitySection,
  belowIntroSection,
}) => {
  // Everything below used to initialize from literals, so the page forgot the
  // visitor on every refresh. These two initializers are LAZY on purpose: they
  // read the remembered result once, before first paint, so a returning visitor
  // never flashes the cold intro they've already been through.
  const [restored, setRestored] = useState<StoredDiagnostic | null>(readDiagnosticResult);
  // The answer-in-email CREDIT: `/q?qid=13&a=B` sends the visitor here as
  // `/try?exam=LMSW&prefill=13:B`. They already answered that question (and /q
  // already showed them its reveal), so it is seeded into the answer sheet and
  // dropped from the questions we present — never asked twice, and scored exactly
  // as if they'd answered it live (a wrong email pick still counts wrong, so the
  // readiness number stays honest).
  //
  // Ignored for a RETURNING visitor: they already have a whole remembered run, and
  // seeding a single answer over it would recompute their score from that one
  // question. A malformed or non-bank prefill parses to null and this page then
  // behaves exactly as if the param weren't there.
  const [saved] = useState(() => (restored ? null : readDiagnosticProgress()));
  // A saved run without the prefill's answer was retaken clean; ?prefill= outlives the retake in the URL.
  const [prefill, setPrefill] = useState(() => {
    if (restored) return null;
    const parsed = parseDiagnosticPrefill(typeof window === 'undefined' ? null : new URLSearchParams(window.location.search).get('prefill'));
    return parsed && saved && !(parsed.questionId in saved.answers) ? null : parsed;
  });
  // The questions actually PRESENTED. Identical to DIAGNOSTIC without a prefill;
  // scoring below always runs over the full DIAGNOSTIC, so the denominator stays 6.
  const sequence = useMemo(
    () => (prefill ? DIAGNOSTIC.filter((q) => q.id !== prefill.questionId) : DIAGNOSTIC),
    [prefill]
  );
  // Mid-quiz reload (iOS Safari discards backgrounded tabs) resumes on the same question.
  const [progress] = useState(() => {
    const resumeIdx = saved ? sequence.findIndex((q) => q.id === saved.questionId) : -1;
    return saved && resumeIdx >= 0 ? { ...saved, idx: resumeIdx } : null;
  });
  // The score is HELD behind the email gate, so a returning visitor routes by
  // whether they already captured: captured -> straight to their revealed report;
  // not captured -> back to the held reveal (never a screen that leaks the number).
  const [stage, setStage] = useState<'intro' | 'quiz' | 'capture' | 'report'>(() =>
    restored ? (restored.captured ? 'report' : 'capture') : progress ? 'quiz' : 'intro'
  );
  // The signed Pass Plan download token — from a remembered capture or set live
  // when the visitor captures in this session. Unlocks the gated PDF link.
  const [downloadToken, setDownloadToken] = useState<string | undefined>(() => restored?.downloadToken);
  const [idx, setIdx] = useState(() => progress?.idx ?? 0);
  const [answers, setAnswers] = useState<Record<number, string>>(() =>
    progress?.answers ?? (prefill ? { [prefill.questionId]: prefill.letter } : {})
  );
  // How many of the six are already done before the first presented question.
  const seededCount = DIAGNOSTIC.length - sequence.length;
  // The letter picked for the CURRENT question. Non-null = show Pam's interjection
  // (answered, awaiting Continue). Reset to null on advance.
  const [picked, setPicked] = useState<string | null>(() => progress?.picked ?? null);
  const [showReasoning, setShowReasoning] = useState(false);
  const reduceMotion = useReducedMotion();
  const [examType, setExamType] = useState<DiagnosticExamType | null>(
    () => restored?.examType ?? progress?.examType ?? (prefill ? readExamParam() : null)
  );
  const [examStatus, setExamStatus] = useState<ExamStatus | null>(null);

  // /try is ONE landing for many simultaneous funnels (Substack, Reddit, partners…).
  // Capture the campaign/source (+ existing `exam`) from the URL once on mount so we
  // can stamp it onto EVERY diagnostic event — making each event attributable to the
  // funnel that drove it, even in event-scoped GA4 queries.
  const attribution = useMemo<Record<string, string>>(() => {
    const out: Record<string, string> = {};
    if (typeof window === 'undefined') return out;
    const p = new URLSearchParams(window.location.search);
    for (const key of ['utm_source', 'utm_medium', 'utm_campaign', 'exam'] as const) {
      const v = p.get(key);
      if (v) out[key] = v;
    }
    // `src` is the INTERNAL referrer for same-site CTAs (e.g. /pass-plan → /try).
    // Those links deliberately carry no utm_* — UTMs on an internal link overwrite
    // the visitor's real acquisition campaign — so without this, an internal
    // journey would leave no trace at all on the diagnostic events it produced.
    const src = p.get('src');
    if (src) out.internal_src = src;
    return out;
  }, []);

  // Single choke point for every /try event. Merges campaign attribution so
  // simultaneous funnels stay distinguishable. If this device is flagged internal
  // (?internal=1), events are SUPPRESSED entirely — the founder's own testing then
  // produces zero GA4 events, so it can't pollute the numbers and needs no
  // downstream filter or GA4 custom-dimension setup.
  const auth = useOptionalAuth();
  const gate = usePublicFunnelGate(gateSignedInArrivals, Boolean(auth?.currentUser), auth?.isAuthLoading ?? false);
  const track = useCallback((name: string, extra: Record<string, unknown> = {}) => gate(() => {
    if (isInternalTraffic()) return;
    trackGtagEvent(name, { ...attribution, diagnostic_variant: 'classic', ...extra });
  }), [attribution, gate]);

  // A/B on the email-gate copy. Exposure and the lead event carry the arm on `variant`,
  // the GA4 dimension the readout reads; the six-turn split rides `diagnostic_variant`.
  const [gateVariant] = useState(() => abVariant(GATE_EXPERIMENT));
  const gateTrack = useCallback(
    (name: string, extra: Record<string, unknown> = {}) =>
      track(name, { ...extra, experiment: GATE_EXPERIMENT, variant: gateVariant }),
    [track, gateVariant],
  );
  const gateExposedRef = useRef(false);
  useEffect(() => {
    // Re-armed off capture so a retake's second completion gets its own capture view.
    if (stage !== 'capture') {
      gateExposedRef.current = false;
      return;
    }
    if (gateExposedRef.current) return;
    gateExposedRef.current = true;
    gateTrack('diagnostic_capture_viewed');
  }, [stage, gateTrack]);

  // Held until auth settles: Landing unmounts this for a signed-in visitor, who must never count as a view.
  const authLoading = auth?.isAuthLoading ?? false;
  const viewTrackedRef = useRef(false);
  useEffect(() => {
    if (authLoading || viewTrackedRef.current) return;
    viewTrackedRef.current = true;
    applyInternalTrafficFromQuery(); // honor ?internal=1 / ?internal=0 before we emit
    track('diagnostic_viewed');
  }, [track, authLoading]);

  // Distinguishes "a stranger landed" from "someone came back", which the funnel
  // could not see at all while the diagnostic had no memory. Fired once on mount
  // when a remembered run exists (the visitor no longer sees a distinct
  // "returning" screen: captured ones land on the report, the rest on the held
  // reveal), with `captured` so the two return paths stay separable.
  const returningLoggedRef = useRef(false);
  useEffect(() => {
    if (!restored || returningLoggedRef.current) return;
    returningLoggedRef.current = true;
    track('diagnostic_returning_viewed', {
      score: restored.correctCount,
      examType: restored.examType ?? 'unknown',
      weakArea: restored.weak[0] ?? 'none',
      captured: restored.captured,
    });
  }, [restored, track]);

  const start = () => {
    // The prefill params are added ONLY on a credited start, so the existing
    // diagnostic_started payload is byte-identical for every other visitor.
    track('diagnostic_started', {
      examType: examType ?? 'unknown',
      ...(prefill ? { prefilled: true, prefillQuestionId: prefill.questionId } : {}),
    });
    // Mirror the GA4 event at the SAME trigger for the ad pixels, so every
    // channel reports on one denominator: cost-per-diagnostic-start. Meta gets
    // a custom event; Reddit gets ViewContent (built-in, reports today) plus a
    // custom 'DiagnosticStart' (clean long-term). All no-op for internal
    // traffic / non-prod builds.
    gate(() => {
      trackMetaEvent('diagnostic_started', { examType: examType ?? 'unknown' });
      trackRedditEvent('ViewContent');
      trackRedditCustomEvent('DiagnosticStart', { examType: examType ?? 'unknown' });
    });
    setStage('quiz');
  };

  // Answering RECORDS the pick and surfaces Pam's interjection; it no longer jumps
  // straight to the next question. `advance` (the Continue button) moves on.
  const choose = (letter: string) => {
    if (picked) return; // one answer per question; ignore clicks once revealed
    const q = sequence[idx];
    // Per-question event — lights up the started→Q1→…→Q6→completed drop-off that
    // was previously a black box between started and completed. `index` stays the
    // position within the six (seeded answers shift it), so the funnel steps line up.
    track('diagnostic_question_answered', {
      index: seededCount + idx,
      questionId: q.id,
      isCorrect: letter === q.correct,
      examType: examType ?? 'unknown',
    });
    setAnswers((prev) => ({ ...prev, [q.id]: letter }));
    setPicked(letter);
  };

  const advance = () => {
    if (idx + 1 < sequence.length) {
      setIdx((i) => i + 1);
      setPicked(null);
    } else {
      track('diagnostic_completed', { score: correctCount, total: DIAGNOSTIC.length, examType: examType ?? 'unknown' });
      // Remember the result (90-day TTL, never for internal traffic) so a refresh
      // or a visit next week greets them with their own number instead of the
      // cold "Which exam are you taking?" screen they already answered. Stored as
      // NOT-captured: the score is held until they trade their email, and a return
      // before that puts them back on the held reveal, not the report.
      saveDiagnosticResult({ examType, correctCount, readiness, weak, captured: false });
      clearDiagnosticProgress();
      // HOLD the score. The reveal is the reward for the email, so completing the
      // six questions lands on the capture stage (blurred gauge + email), never
      // straight on the ungated report.
      setStage('capture');
    }
  };

  useEffect(() => {
    if (stage === 'quiz') saveDiagnosticProgress({ examType, answers, questionId: sequence[idx].id, picked });
  }, [stage, examType, answers, sequence, idx, picked]);

  // The capture's background redeem makes their account. 'pending' holds the primary
  // button until it lands: a click mid-mint would abort it and collide at /onboarding.
  const [account, setAccount] = useState<'pending' | 'saved' | 'done' | null>(null);
  // Backend grants the trial at mint; userData is the truth, read once the redeem signed them in.
  const userData = auth?.userData;
  const trialRunning = userData?.isTrialActive === true;
  const paid = hasPaidAccess(userData);
  const trialStarted = isPassPlanTrialAtMintEnabled() && account === 'saved' && trialRunning;
  const [handoff] = useState(handoffVariant);
  // Only a capture in THIS session hands off; a returning-captured visitor keeps the report.
  const [capturedLive, setCapturedLive] = useState(false);
  // Only a redeem that CREATED the account starts a fresh Pass Plan; an existing one keeps the report.
  const [accountCreated, setAccountCreated] = useState(false);
  const [handoffStep, setHandoffStep] = useState<'waiting' | 'ready' | 'failed'>('waiting');

  /**
   * Capture succeeded: persist the flag + token (so a return isn't re-gated),
   * then flip to the report — whose FilledReadinessGauge draws from 0 to the
   * score on mount. That mount IS the reveal animation.
   */
  const onCaptured = (token?: string) => {
    markDiagnosticCaptured(token);
    if (token) savePassPlanToken(token);
    setDownloadToken(token);
    setAccount((a) => a ?? (token ? 'pending' : null));
    setCapturedLive(true);
    setStage('report');
  };

  /** "Retake the diagnostic": forget the stored result and start clean. */
  const retake = () => {
    track('diagnostic_retake_clicked', { examType: examType ?? 'unknown' });
    clearDiagnosticResult();
    clearDiagnosticProgress();
    setRestored(null);
    // A retake is a CLEAN run of all six: drop the email credit too, or the
    // retaken diagnostic would silently be five questions out of six.
    setPrefill(null);
    setAnswers({});
    setIdx(0);
    setPicked(null);
    setDownloadToken(undefined);
    setStage('intro');
  };

  // Celebrate reaching the results — a single gentle burst in brand colors as the
  // readiness gauge reveals. Celebrates FINISHING (and the reveal), not a score, so
  // it fires regardless of how they did. Reduced-motion users get no burst.
  // Fired on a canvas this page owns and unmounts with (see useOwnedConfetti):
  // the shared global canvas outlived the route, and resetting IT would have
  // cancelled CelebrationOverlay's burst for the same milestone.
  const { canvasRef: confettiCanvasRef, fire: fireConfetti } = useOwnedConfetti();
  useEffect(() => {
    if (stage !== 'report' || reduceMotion) return;
    fireConfetti({
      particleCount: 90,
      spread: 72,
      origin: { y: 0.35 },
      colors: ['#2C5F5D', '#F5B53D', '#8B5A7C', '#FFFFFF'],
      disableForReducedMotion: true,
    });
  }, [stage, reduceMotion, fireConfetti]);

  // The exam picker sits below the fold on phones, so entering the quiz (and
  // each question advance) must bring the question card back into view. Without
  // this, the first "Start my diagnostic" tap left the new card off-screen above
  // the scroll position — mobile visitors saw blank space + footer and read it
  // as a dead click (QA stranger-walk, 2026-08-12).
  const quizCardRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (stage !== 'quiz') return;
    // Optional call: jsdom (vitest) has no scrollIntoView.
    quizCardRef.current?.scrollIntoView?.({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
  }, [stage, idx, reduceMotion]);

  // "Reveal my readiness" swaps the last question for a much shorter capture
  // card, and the page kept the old scroll offset: the card landed underneath
  // the sticky h-16 header, so the email field (the whole point of the stage)
  // was clipped. The card carries scroll-mt-20 so `block: 'start'` stops below
  // the header rather than behind it.
  const captureCardRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (stage !== 'capture') return;
    // Optional call: jsdom (vitest) has no scrollIntoView.
    captureCardRef.current?.scrollIntoView?.({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
  }, [stage, reduceMotion]);

  // Same again for the reveal: a phone keyboard leaves the page scrolled to the email
  // field (699px on iPhone 15), which put the score and its one button above the viewport.
  const reportCardRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (stage !== 'report') return;
    reportCardRef.current?.scrollIntoView?.({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
  }, [stage, reduceMotion]);

  // Also emits the canonical `subscription_clicked` (with a distinct source) so
  // the diagnostic reveal shows up in the SAME GA4 funnel as every other door,
  // instead of only in the diagnostic-only event nobody joins on.
  const onSubscribe = (plan: 'fullAccess') => {
    track('diagnostic_subscribe_clicked', { plan });
    track('subscription_clicked', {
      subscriptionStatus: null,
      isLoggedIn: false,
      source: 'diagnostic_reveal',
      timestamp: Date.now(),
    });
  };
  const onTrial = () => track('diagnostic_trial_clicked', { examType: examType ?? 'unknown' });

  // Derived from answers (used by the report + the email capture). Weakness is
  // aggregated BY AREA (2 questions each), NOT per-question: an area is weak if it
  // has any miss, and weak areas rank weakest-first, ties broken by exam weight
  // (AREAS order) so a tie resolves to where a gap costs the most on the real exam.
  // Six items can only rank the areas we SAMPLED, never the whole blueprint — the
  // report copy is careful to say "sampled," not "your weakest area overall."
  const correctCount = DIAGNOSTIC.filter((q) => answers[q.id] === q.correct).length;
  const readiness = Math.round((correctCount / DIAGNOSTIC.length) * 100);
  const areaCorrect = (area: string) =>
    DIAGNOSTIC.filter((q) => q.cat === area && answers[q.id] === q.correct).length;
  const areaTotal = (area: string) => DIAGNOSTIC.filter((q) => q.cat === area).length;
  // weakest-first: fewest correct wins; ties broken by exam weight (AREAS order).
  const rankedAreas = [...AREAS].sort(
    (a, b) => areaCorrect(a) - areaCorrect(b) || AREAS.indexOf(a) - AREAS.indexOf(b),
  );
  const weak = rankedAreas.filter((area) => areaCorrect(area) < areaTotal(area));
  const strong = AREAS.filter((area) => areaCorrect(area) === areaTotal(area));

  // A returning-captured visitor reaches the report with NO live answers (we never
  // store the answer sheet, only the summary), so the report reads from the
  // remembered run instead. `strong` isn't stored, but an area is either weak or
  // strong, so the strong areas are exactly the ones not in the remembered weak list.
  const hasLiveAnswers = Object.keys(answers).length > 0;
  const reportReadiness = hasLiveAnswers ? readiness : restored?.readiness ?? readiness;
  const reportCorrect = hasLiveAnswers ? correctCount : restored?.correctCount ?? correctCount;
  const reportWeak = hasLiveAnswers ? weak : restored?.weak ?? weak;
  const reportStrong = hasLiveAnswers ? strong : AREAS.filter((area) => !reportWeak.includes(area));
  const reportExam = examType ?? restored?.examType ?? null;

  const handoffBridge =
    stage === 'report' && handoff !== 'A' && capturedLive && handoffStep !== 'failed' &&
    (account === 'pending' || (account === 'saved' && accountCreated));
  // What this capture actually showed: the forward ('ready') shows the variant; any
  // fallback to the report (existing account, failed redeem or profile) is A.
  const handoffShown =
    stage !== 'report' || !capturedLive ? null : handoffStep === 'ready' ? handoff : handoffBridge ? null : 'A';
  useEffect(() => {
    if (!handoffShown) return;
    track('handoff_viewed', { handoff_variant: handoffShown });
    armHandoffFirstAnswer(handoffShown);
  }, [handoffShown, track]);
  // The aim finish's profile write + plan warm-up, once userData (accessStatus) has
  // loaded, so /start takes the day-1 branch. A failed write falls back to the report.
  const handoffUserLoaded = Boolean(userData) && !auth?.isLoading;
  const handoffStartedRef = useRef(false);
  // The forward lands on two lazy routes; loading their chunks now keeps the bridge
  // on screen instead of a blank Suspense fallback after it.
  useEffect(() => {
    if (!handoffBridge) return;
    void import('@/pages/StartQuizRedirect');
    void import('@/pages/QuizApp');
  }, [handoffBridge]);
  useEffect(() => {
    if (!handoffBridge || account !== 'saved' || !handoffUserLoaded || !reportExam || handoffStartedRef.current) return;
    handoffStartedRef.current = true;
    void (async () => {
      try {
        await saveAimProfile({ exam: reportExam, date: null, retake: false }, auth?.currentUser, userData?.profile);
      } catch {
        setHandoffStep('failed');
        return;
      }
      await warmTrialPlan();
      const uid = auth?.currentUser?.uid;
      if (uid) saveHandoffResults({ uid, correct: reportCorrect, total: DIAGNOSTIC.length, weak: reportWeak, exam: reportExam });
      clearPassPlanMint();
      setHandoffStep('ready');
    })();
  }, [handoffBridge, account, handoffUserLoaded, reportExam, reportWeak, reportCorrect, auth?.currentUser, userData?.profile]);
  // userData can fail to load and never retry (useUserDataState sets it null);
  // without this the bridge's Keep going stays disabled forever.
  useEffect(() => {
    if (!handoffBridge || account !== 'saved' || handoffUserLoaded) return;
    const timer = setTimeout(() => setHandoffStep('failed'), HANDOFF_PROFILE_WAIT_MS);
    return () => clearTimeout(timer);
  }, [handoffBridge, account, handoffUserLoaded]);

  // The lead payload for the capture form works from live answers on a fresh run
  // and from the remembered summary for a returning-not-captured visitor.
  const captureLeadPayload = {
    score: reportCorrect,
    total: DIAGNOSTIC.length,
    readiness: reportReadiness,
    weakAreas: reportWeak,
    examType: reportExam ?? 'unknown',
  };

  // ---- CAPTURE (the held reveal) ----
  // The score is computed and remembered, but withheld: the gauge is LOCKED and
  // no number, breakdown, or rationale is on screen. The email is the key that
  // turns it. Reached two ways: finishing the six questions (fresh), or returning
  // before ever capturing (`!hasLiveAnswers`). Same anticipation either way.
  if (stage === 'capture') {
    const weakest = reportWeak[0] ?? null;
    return (
      <Shell title="Your ASWB readiness is ready" canonicalPath={canonicalPath} metaDescription={metaDescription}>
        <div
          ref={captureCardRef}
          className="scroll-mt-20 rounded-2xl border-2 border-neutral-200 overflow-hidden mb-5"
          style={card}
          data-testid="diagnostic-capture"
        >
          {/* Teal hold zone: the locked gauge + Pam's anticipation, mirroring the
              reveal card's layout so the swap to the filled gauge lands in place. */}
          <div className="p-6 md:p-7" style={{ backgroundColor: '#2C5F5D' }}>
            <div className="flex flex-col sm:flex-row items-center gap-6">
              <LockedReadinessGauge />
              <div className="text-center sm:text-left">
                <span className="text-[11px] font-extrabold uppercase tracking-[0.15em] text-white/60">Your starting point</span>
                <h1 className="text-2xl md:text-[1.75rem] font-display font-bold text-white mt-1 mb-1">Your readiness is ready.</h1>
                <p className="text-white/80">
                  {gateVariant === 'B'
                    ? 'See exactly where you stand.'
                    : <>You&apos;re closer than you think. Enter your email and I&apos;ll show you exactly where you stand, plus send your Pass Plan.</>}
                </p>
              </div>
            </div>
          </div>
          {/* White action zone: the email is the only thing on screen, so it can't
              be out-competed by an already-visible result the way it used to be. */}
          <div className="bg-white p-6 md:p-7">
            <PassPlanCapture
              track={gateTrack}
              shortCopy={gateVariant === 'B'}
              weakest={weakest}
              lead={captureLeadPayload}
              onCaptured={onCaptured}
              onRedeemed={(r) => {
                setAccountCreated(r.created);
                setAccount(r.outcome === 'signed-in' ? 'saved' : 'done');
              }}
              source="try_capture"
              diagnosticVariant="classic"
            />
            {/* Returning-not-captured visitors land here too; give them a way back
                to a clean run rather than trapping them at the gate. */}
            {!hasLiveAnswers && (
              <button
                onClick={retake}
                data-testid="diagnostic-retake"
                className="mt-4 w-full px-4 py-2.5 text-sm text-neutral-500 hover:text-neutral-700 font-semibold"
              >
                Or retake the diagnostic
              </button>
            )}
            <p className="text-center text-xs text-neutral-400 mt-4">{introFootnote ?? 'Pam, your clinical supervisor'}</p>
          </div>
        </div>
      </Shell>
    );
  }

  // ---- INTRO ----
  if (stage === 'intro') {
    // Avoid two <h1>s on the page: when a hero (with its own h1) is supplied,
    // the intro card's heading demotes to h2.
    const IntroHeading = heroSection ? 'h2' : 'h1';
    return (
      <Shell
        title={introPageTitle}
        canonicalPath={canonicalPath}
        metaDescription={metaDescription}
        ogTitle={ogTitle}
        ogImage={ogImage}
        structuredData={structuredData}
      >
        {/* One continuous card: the (optional) teal hero zone flows full-bleed into
            the white action zone below it — overflow-hidden clips the hero's square
            top to the card's radius, and the wrapper owns the single border + ledge. */}
        <div className="rounded-2xl border-2 border-neutral-200 overflow-hidden" style={card}>
          {heroSection}
          <div className={`bg-white text-center flex flex-col ${attachedHero ? 'px-5 pt-4 pb-6 md:px-7 md:pt-6 md:pb-7' : 'px-5 py-6 md:p-7'}`}>
            {!attachedHero && (
              <>
                <span className="text-xs font-bold uppercase tracking-wider text-primary-600 bg-primary-50 px-4 py-2 rounded-full inline-block self-center mb-4 md:mb-5">Free · no account · 3 min</span>
                <IntroHeading className="text-3xl md:text-4xl font-display font-bold text-neutral-900 mb-3">Free ASWB practice test: where do you actually stand?</IntroHeading>
                <p className="text-neutral-600 order-1 md:order-none mt-6 mb-0 md:mt-0 md:mb-2">6 real practice questions in the new 2026 format (3 content areas, 3 answer choices) for LCSW, LMSW, or LBSW. Finish them, enter your email, and you get your <strong>readiness score</strong>, your <strong>weakest areas</strong>, and a Pass Plan that says exactly what to study next. No account, no card.</p>
                <p className="text-neutral-500 text-sm order-2 md:order-none mt-3 mb-0 md:mt-0 md:mb-4">Especially if you've taken it before: let's find why, not just <em>that</em>, you missed.</p>
              </>
            )}
            <p className="text-xs font-semibold text-warning-800 bg-warning-light border border-warning/40 rounded-lg px-3 py-2 inline-block self-center order-3 md:order-none mt-4 mb-0 md:mt-0 md:mb-6">
              More than 1 in 4 first-time candidates don't pass this exam. A retake means months of delay
              and another exam fee. Worth knowing exactly where you stand before test day.
            </p>
            {/* The answer-in-email credit, acknowledged before they pick. Only
                rendered on a credited entry, so the standard intro is untouched. */}
            {prefill && (
              <p
                data-testid="diagnostic-prefill-credit"
                className="text-sm font-semibold text-primary-800 bg-primary-50 border-2 border-primary-100 rounded-xl px-4 py-3 mb-6"
              >
                You&apos;ve answered 1 of the 6 already. Pick your exam and finish the other five.
              </p>
            )}
            <p className="text-sm font-semibold text-neutral-700 mb-2">Which exam are you taking?</p>
            <div className="flex gap-2 justify-center mb-4 md:mb-6">
              {(['LBSW', 'LMSW', 'LCSW'] as const).map((ex) => (
                <button key={ex} onClick={() => setExamType(ex)}
                  className={`px-5 py-2.5 rounded-xl border-2 font-bold transition-colors ${examType === ex ? 'bg-primary-600 text-white border-primary-700' : 'bg-white text-neutral-700 border-neutral-200 hover:border-primary-300'}`}>
                  {ex}
                </button>
              ))}
            </div>
            <button onClick={start} disabled={!examType} className="w-full px-4 py-3.5 bg-primary-600 hover:bg-primary-700 text-white rounded-xl font-bold text-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed" style={{ boxShadow: examType ? '0 4px 0 #1B3A38' : 'none' }}>{examType ? (prefill ? 'Continue my diagnostic →' : 'Start my diagnostic →') : 'Pick your exam to start'}</button>
            <p className="text-center text-xs text-neutral-400 order-4 md:order-none mt-4">{introFootnote ?? 'Pam, your clinical supervisor'}</p>
          </div>
        </div>
        {belowIntroSection}
      </Shell>
    );
  }

  // ---- QUIZ ----
  if (stage === 'quiz') {
    const q = sequence[idx];
    const answered = picked !== null;
    const isLast = idx + 1 === sequence.length;
    // Position within the SIX, so a credited email answer already counts toward
    // the total. Without a prefill `seededCount` is 0 and every number below is
    // exactly what it always was.
    const position = seededCount + idx + 1;
    // The bar ticks forward the instant they answer (before Continue) for momentum.
    const progress = ((seededCount + idx + (answered ? 1 : 0)) / DIAGNOSTIC.length) * 100;
    return (
      <Shell title={`Question ${position} of ${DIAGNOSTIC.length}`} canonicalPath={canonicalPath} metaDescription={metaDescription}>
        <div ref={quizCardRef} className="bg-white rounded-2xl border-2 border-neutral-200 p-6 md:p-7 scroll-mt-20" style={card}>
          <div className="flex items-center justify-between mb-4">
            <span className="text-xs font-semibold uppercase tracking-wide text-primary-600">{q.cat}</span>
            <span className="text-xs font-semibold text-neutral-400">{position} / {DIAGNOSTIC.length}</span>
          </div>
          <div className="w-full h-1.5 bg-neutral-100 rounded-full mb-5"><div className="h-1.5 bg-primary-500 rounded-full transition-all duration-500" style={{ width: `${progress}%` }} /></div>
          <p className="text-neutral-900 text-lg leading-relaxed mb-5">{q.stem}</p>
          <div className="space-y-3">
            {q.options.map((opt) => {
              const chosen = picked === opt.l;
              // No right/wrong colouring: the chosen option is simply marked (teal),
              // the rest dim. Correctness stays hidden until the readiness reveal.
              const cls = answered
                ? chosen
                  ? 'border-primary-500 bg-primary-50 cursor-default'
                  : 'border-neutral-200 bg-white opacity-45 cursor-default'
                : 'border-neutral-200 bg-white hover:border-primary-400 hover:bg-primary-50';
              return (
                <button key={opt.l} onClick={() => choose(opt.l)} disabled={answered} data-testid="diagnostic-option"
                  className={`w-full text-left flex items-start gap-3 px-4 py-3 rounded-xl border-2 transition-colors ${cls}`}>
                  <span className={`font-bold flex-shrink-0 ${answered && chosen ? 'text-primary-700' : 'text-neutral-500'}`}>{opt.l}</span>
                  <span className="text-neutral-800">{opt.text}</span>
                </button>
              );
            })}
          </div>
          {answered && <PamInterjection q={q} isLast={isLast} onContinue={advance} />}
        </div>
      </Shell>
    );
  }

  // ---- HANDOFF B/C: no report card; straight into day 1 once the account lands ----
  // A failed, token-less or existing-account redeem has no fresh plan to start, so it keeps the report.
  if (handoffBridge) {
    const startPath = handoffStartPath(reportExam);
    return (
      <Shell title="Your Pass Plan starts now" canonicalPath={canonicalPath} metaDescription={metaDescription}>
        {handoffStep === 'ready' && <ForwardTo to={startPath} />}
        <div className="rounded-2xl border-2 border-neutral-200 overflow-hidden mb-5" style={card} data-testid="handoff-bridge">
          <div className="p-6 md:p-7" style={{ backgroundColor: '#2C5F5D' }}>
            <span className="text-[11px] font-extrabold uppercase tracking-[0.15em] text-white/60">Pass Plan · Day 1 of 7</span>
            <h1 className="text-2xl md:text-[1.75rem] font-display font-bold text-white mt-1 mb-1">Your plan starts now.</h1>
            <p className="text-white/80">
              {reportWeak[0] ? `Day 1 opens with ${reportWeak[0]}, where your answers leaned weakest.` : 'Day 1 opens with your first module.'}
            </p>
            {handoffStep === 'ready' ? (
              <InAppLink
                data-testid="handoff-keep-going"
                to={startPath}
                className="mt-4 block text-center px-6 py-3 rounded-xl bg-white text-primary-800 font-bold text-lg"
                style={{ boxShadow: '0 4px 0 #1B3A38' }}
              >
                Keep going
              </InAppLink>
            ) : (
              <span aria-busy="true" className="mt-4 block text-center px-6 py-3 rounded-xl bg-white/70 text-primary-800 font-bold text-lg">
                Keep going
              </span>
            )}
          </div>
        </div>
      </Shell>
    );
  }

  // ---- REPORT ----
  const trialPath = `/onboarding?exam=${reportExam ?? ''}${examStatus ? `&examStatus=${examStatus}` : ''}&src=${canonicalPath === '/' ? 'home' : 'try'}_to_trial`;
  // Option C: onboarding is the single doorway into the trial, so the results
  // screen carries exactly one CTA into it (src=trial_doorway) instead of two
  // links to the same destination under different copy. Gated on account ===
  // 'saved' (not just !== 'pending'), which also covers 'done' (redeem failed)
  // and null (no token at all) — sending an unauthenticated visitor into the
  // doorway's account-less finish would silently lose everything they answer.
  const doorwayEnabled = isTrialWeekOptionCEnabled() && account === 'saved';
  const doorwayPath = `/onboarding?exam=${reportExam ?? ''}&src=trial_doorway`;
  const fullAccessHref = isEmbeddedCheckoutEnabled() ? appHref('/checkout') : PAYMENT_LINKS.fullAccess;
  return (
    <Shell title="Your ASWB diagnostic results" canonicalPath={canonicalPath} metaDescription={metaDescription}>
      {/* Readiness reveal — one card, two zones (mirrors the entry): the teal "your
          number" zone with the FILLED gauge that pays off the homepage's "?", then
          the white breakdown zone. */}
      {/* Only when it can actually draw. This is a fixed full-viewport element,
          so a reduced-motion visitor (who never gets a burst) should not have it
          laid over the report at all. */}
      {!reduceMotion && (
        <canvas
          ref={confettiCanvasRef}
          data-testid="diagnostic-confetti-canvas"
          className={OWNED_CONFETTI_CANVAS_CLASS}
          aria-hidden="true"
        />
      )}
      <div ref={reportCardRef} className="scroll-mt-20 rounded-2xl border-2 border-neutral-200 overflow-hidden mb-5" style={card} data-testid="diagnostic-report">
        <div className="p-6 md:p-7" style={{ backgroundColor: '#2C5F5D' }}>
          <div className="flex flex-col sm:flex-row items-center gap-6">
            {/* The reveal: this gauge mounts on the flip from 'capture', so its arc
                draws from 0 to the score. That mount IS the reward animation. */}
            <FilledReadinessGauge score={reportReadiness} />
            <div className="text-center sm:text-left">
              <h1 className="text-2xl font-display font-bold text-white mb-1">You got {reportCorrect} of {DIAGNOSTIC.length}.</h1>
              <p className="text-white/80">Six questions cannot tell either of us whether you are ready for the {reportExam ?? 'ASWB'}, and I will not pretend otherwise. What they can show is where your answers leaned today.</p>
              {account === 'saved' && (
                <p className="text-white/80 mt-3" data-testid="try-account-saved">Your account is set up and this score is saved to it.</p>
              )}
              {account === 'pending' ? (
                <span aria-busy="true" className="mt-4 block sm:inline-block text-center px-6 py-3 rounded-xl bg-white/70 text-primary-800 font-bold text-lg">
                  Saving your score…
                </span>
              ) : (
                <InAppLink
                  data-testid="try-start-plan-cta"
                  to={doorwayEnabled ? doorwayPath : trialPath}
                  onClick={() => track('diagnostic_start_plan_clicked', { examType: reportExam ?? 'unknown', signedIn: account === 'saved' })}
                  className="mt-4 block sm:inline-block text-center px-6 py-3 rounded-xl bg-white text-primary-800 font-bold text-lg"
                  style={{ boxShadow: '0 4px 0 #1B3A38' }}
                >
                  {doorwayEnabled ? 'See your 7-day plan' : 'Start your plan'}
                </InAppLink>
              )}
            </div>
          </div>
        </div>
        {/* The breakdown slides/fades in just behind the gauge fill, so the whole
            card reads as one orchestrated reveal (reduced motion shows it at rest). */}
        <motion.div
          className="bg-white p-6 md:p-7"
          initial={reduceMotion ? false : { opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: 'easeOut', delay: reduceMotion ? 0 : 0.35 }}
        >
          <ResultsBreakdown weak={reportWeak} strong={reportStrong} />
          {/* Honest framing: 6 questions across 3 areas can rank the areas we SAMPLED,
              not the full 2026 blueprint. Don't overclaim a definitive weakest area. */}
          <p className="text-xs text-neutral-400 mt-3">Based on the 3 content areas we sampled here. A full timed exam sharpens the picture.</p>

          {/* The packet is the reward the email unlocked, so the report offers the
              DOWNLOAD (not the form again). /pass-plan reads the signed token from
              localStorage, never the href, where Clarity replays would record it
              (#604); with none stored it falls back to the unlock form. */}
          <div className="mt-5 pt-5 border-t-2 border-neutral-100 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <p className="text-sm font-bold text-neutral-900">📘 Your 2026 Pass Plan is unlocked</p>
              <p className="text-sm text-neutral-600">It&apos;s in your inbox, and yours to grab right here.</p>
            </div>
            <a
              data-testid="pass-plan-download"
              data-clarity-mask="true"
              href={appHref('/pass-plan?src=diagnostic_reveal')}
              onClick={() => {
                if (downloadToken) savePassPlanToken(downloadToken);
                track('diagnostic_pass_plan_clicked', { examType: reportExam ?? 'unknown', signedIn: account === 'saved' });
              }}
              className="flex-shrink-0 inline-flex items-center justify-center gap-2 px-5 py-3 bg-white hover:bg-primary-50 text-primary-700 border-2 border-primary-600 rounded-xl font-bold transition-colors"
              style={{ boxShadow: '0 3px 0 rgba(27,58,56,0.18)' }}
            >
              <BookOpen className="w-[18px] h-[18px]" /> Download the Pass Plan
            </a>
          </div>
          {trialStarted && (
            <a
              data-testid="try-trial-started-cta"
              href={appHref('/dashboard')}
              onClick={() => track('diagnostic_trial_dashboard_clicked', { examType: reportExam ?? 'unknown' })}
              className="btn-primary mt-5 w-full rounded-full"
            >
              Your free week has started. Go to your dashboard
            </a>
          )}
        </motion.div>
      </div>

      {account === 'saved' && (
        <div className="mb-5">
          <AppHandoffCard variant="result" examDay={examDayLabel(userData?.profile)} email={auth?.currentUser?.email} />
        </div>
      )}

      {/* Equity statement (homepage only) — stated plainly, right after the result. */}
      {equitySection}

      {/* Optional post-result segmentation — "when are you testing?" (design §7b).
          Appears AFTER the result, Skip is one tap; it NEVER gates the diagnostic. */}
      <div className="bg-white rounded-2xl border-2 border-neutral-200 p-5 md:p-6 mb-5" style={card}>
        <div className="flex items-center justify-between gap-3 mb-1">
          <p className="text-sm font-semibold text-neutral-900">🗓️ When are you testing?</p>
          {examStatus && (
            <button
              onClick={() => setExamStatus(null)}
              data-testid="try-status-skip"
              className="text-xs text-neutral-400 hover:text-neutral-600 underline"
            >
              Clear
            </button>
          )}
        </div>
        <p className="text-sm text-neutral-600 mb-3">
          Optional, but it helps me tailor your plan. Your date often depends on board approval, so pick whatever's true.
        </p>
        <div className="flex flex-wrap gap-2">
          {SCHEDULE_CHOICES.map(({ status, label }) => (
            <button
              key={status}
              data-testid={`try-status-${status}`}
              onClick={() => { setExamStatus(status); track('diagnostic_schedule_selected', { status }); }}
              className={`px-3.5 py-2 rounded-xl border-2 text-sm font-semibold transition-colors ${
                examStatus === status
                  ? 'bg-primary-600 text-white border-primary-700'
                  : 'bg-white text-neutral-700 border-neutral-200 hover:border-primary-300'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Convert — surfaced right after the results (peak motivation), above the reasoning */}
      {!paid && (
      <div className="rounded-2xl border-2 border-primary-200 bg-primary-50 p-6 md:p-7 mb-5" style={{ boxShadow: '0 6px 0 rgba(27,58,56,0.10)' }}>
        <p className="text-sm font-semibold text-neutral-900 mb-1">That's 6 questions. The full bank adapts to <em>you</em>. It keeps finding your {reportWeak[0] ? <>weak spots like <strong>{reportWeak[0]}</strong></> : 'weak spots'} and drills them until they stick.</p>
        <p className="text-sm text-neutral-700 mb-5">Every question reviewed by Pam Coin, a licensed clinical social worker and Board Approved Clinical Supervisor who supervises clinicians preparing for this exact exam. Plus full mock exams, flashcards, and AI clinical simulations, all built to close gaps like this before test day, not after another retake. <strong>Try it free for 7 days. No card, no billing, nothing to cancel.</strong></p>
        {/* Entry-source tag makes home-vs-try attributable at the CONVERSION event,
            not just via GA4's session landing-page: `/` (Landing wrapper) → home_to_trial,
            `/try` (bare diagnostic) → try_to_trial. Both render this same component, so
            the tag is derived from where it's mounted (canonicalPath). */}
        {/* Two co-primary doors. The $129 option used to be a ghost outline
            button under the trial CTA, so nobody ever reached checkout from the
            single highest-intent moment on the site. Both are now full-weight
            ledge buttons: teal for the free trial, gold for the paid offer. */}
        <div className="flex flex-col sm:flex-row gap-3">
          {/* Doorway ON: the free-trial link above ("See your 7-day plan") is
              already the one primary next step, so this row drops its duplicate
              and keeps only the paid alternative. */}
          {!doorwayEnabled && !trialStarted && (
            <InAppLink data-testid="try-trial-cta" to={trialPath} onClick={onTrial} className="flex-1 block text-center px-4 py-3.5 bg-primary-600 hover:bg-primary-700 text-white rounded-xl font-bold text-lg transition-colors" style={{ boxShadow: '0 4px 0 #1B3A38' }}>Start free for 7 days</InAppLink>
          )}
          <a data-testid="try-fullaccess-cta" href={fullAccessHref} onClick={() => onSubscribe('fullAccess')} className="flex-1 block text-center px-4 py-3.5 rounded-xl font-bold text-lg transition-colors bg-[rgb(var(--color-celebrate))] hover:bg-[rgb(var(--color-celebrate-deep))] text-[rgb(var(--color-on-celebrate))]" style={{ boxShadow: '0 4px 0 rgb(var(--color-celebrate-deep))' }}>Get Full Access, $129</a>
        </div>
        <p className="text-center text-xs font-semibold text-neutral-500 mt-3">One payment. No subscription. 7-day money-back guarantee. Keep access until you pass, retakes included.</p>
        <p className="text-center text-xs text-neutral-400 mt-4">Pam, your clinical supervisor</p>
      </div>
      )}

      {/* The ONE entry point to the post-exam collector (/exam-report). Deliberately
          quiet and deliberately singular: it sits after the conversion block so it
          never competes with it, and it is not repeated anywhere else on the site.
          It is here because this page is where people who have ALREADY tested land
          when they come back to see where they stand. */}
      <p className="text-sm text-neutral-600 text-center mb-5">
        Already took the 2026 exam?{' '}
        <a
          href={appHref('/exam-report?src=diagnostic_report')}
          data-testid="exam-report-entry"
          onClick={() => track('diagnostic_exam_report_clicked', { examType: reportExam ?? 'unknown', signedIn: account === 'saved' })}
          className="font-bold text-primary-700 underline"
        >
          Tell us what it was like
        </a>{' '}
        and we&apos;ll publish what everyone reports, free.
      </p>

      {/* Per-question reasoning — collapsed by default so it never pushes the CTA
          down. Only a fresh completion has the live answer sheet to mark each
          question right/wrong; a returning-captured visitor (whose answers we
          never store) doesn't see this section rather than a misleading all-"Missed". */}
      {hasLiveAnswers && (
      <div className="bg-white rounded-2xl border-2 border-neutral-200 p-5 md:p-6" style={card}>
        <button
          onClick={() => {
            if (!showReasoning) track('diagnostic_reasoning_opened', { examType: reportExam ?? 'unknown', signedIn: account === 'saved' });
            setShowReasoning((v) => !v);
          }}
          className="w-full flex items-center justify-between text-left"
        >
          <span className="text-base font-display font-bold text-neutral-900">See the reasoning on all 6 <span className="font-normal text-neutral-500">and how the exam thinks</span></span>
          <span className="text-2xl text-neutral-400 leading-none">{showReasoning ? '−' : '+'}</span>
        </button>
        {showReasoning && (
          <div className="space-y-4 mt-4 animate-fade-in">
            {DIAGNOSTIC.map((q) => {
              const ok = answers[q.id] === q.correct;
              return (
                <div key={q.id} className="border-b border-neutral-100 pb-4 last:border-0 last:pb-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${ok ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'}`}>{ok ? '✓ Correct' : 'Missed'}</span>
                    <span className="text-xs font-semibold text-neutral-500">{q.cat} · answer {q.correct}</span>
                  </div>
                  <p className="text-sm text-neutral-700">{q.why}</p>
                </div>
              );
            })}
          </div>
        )}
      </div>
      )}
    </Shell>
  );
};

const Shell: React.FC<{
  title: string;
  canonicalPath?: string;
  metaDescription?: string;
  ogTitle?: string;
  ogImage?: string;
  structuredData?: Record<string, unknown>;
  children: React.ReactNode;
}> = ({
  title,
  canonicalPath = '/try',
  metaDescription = DEFAULT_META_DESCRIPTION,
  ogTitle,
  ogImage,
  structuredData,
  children,
  // py-6 on phones hands 32px of fold back to the card; sm+ keeps the old rhythm.
}) => (
  <div
    className="min-h-screen bg-neutral-50 px-4 py-6 sm:py-10 md:py-16"
    data-testid="classic-diagnostic"
  >
    <Helmet>
      <title>{title} · Social Work Test Prep Academy</title>
      {/* Targets the proven "free aswb practice test/exam" search cluster
          (Search Console: our highest-intent impressions; see seo-drafts/targets.md). */}
      <meta name="description" content={metaDescription} />
      {ogTitle && <meta property="og:title" content={ogTitle} />}
      <meta property="og:description" content={metaDescription} />
      <meta property="og:type" content="website" />
      <meta property="og:image" content={ogImage ?? 'https://www.socialworktestprepacademy.com/images/try-og-image.png'} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:image" content={ogImage ?? 'https://www.socialworktestprepacademy.com/images/try-og-image.png'} />
      <link rel="canonical" href={`https://www.socialworktestprepacademy.com${canonicalPath}`} />
      {structuredData && (
        <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
      )}
    </Helmet>
    <div className="max-w-xl mx-auto">{children}</div>
  </div>
);

export default TryAQuestion;
